import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import * as analytics from 'lib/analytics'
import { highlightText } from 'lib/highlight-text'
import {
  setSearchFocused,
  setSearchTerm,
  generateBooleanSearch,
  clearBooleanSearchSuggestions,
  selectBooleanVariant,
  reopenBooleanSuggestions,
  resetActiveBooleanSuggestions
} from 'store/ui/actions'
import { navSubmit } from 'store/search/actions'
import { selectMaybeDoi } from 'store/ui/selectors'
import loginActivateOrUpgrade, { trialCtaLabel } from 'lib/assistant-login-activate-upgrade'
import { userIsAtLeast } from 'lib/user'
import { PLANS } from '../../../server/lib/constants'
import { ChevronRightIcon, HelpIcon, LevelUpIcon, SearchIcon, SparklesIcon } from 'components/icons/Icon'
import Button from 'components/forms/Button'
import CircleSpinner from 'components/misc/CircleSpinner'
import styles from './styles/Dropdown.css'
import { setTab, TABS } from '../../store/home/actions'

const MEANINGFUL_WORD = /[A-Za-z0-9]{3,}/

const hasMeaningfulWord = term =>
  typeof term === 'string' &&
  term
    .trim()
    .split(/\s+/)
    .some(token => MEANINGFUL_WORD.test(token))

const SearchLink = ({ children, icon, ...rest }) => {
  const dispatch = useDispatch()

  return (
    <Link
      data-showdropdown
      data-dropdown-item
      className={styles.searchLink}
      onClick={() => dispatch(setSearchFocused(false))}
      {...rest}
    >
      {icon || <LevelUpIcon className={styles.linkIcon} size='1em' />}
      <span className={styles.itemText}>{children}</span>
    </Link>
  )
}

const BooleanSearchRow = ({ term }) => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const selectedVariant = useSelector(({ ui }) => ui.search.booleanSuggestions?.selectedVariant)
  const isPremium = userIsAtLeast(user, PLANS.premium)
  const enabled = hasMeaningfulWord(term)
  const isReopenMode = !!selectedVariant && selectedVariant.query === term.trim()

  const handleGenerate = () => {
    if (!enabled) return
    if (isReopenMode) {
      analytics.event({
        category: 'Search',
        action: 'Reopen Boolean Suggestions',
        label: term
      })
      dispatch(reopenBooleanSuggestions())
      return
    }
    analytics.event({
      category: 'Search',
      action: 'Click Generate Boolean',
      label: term
    })
    dispatch(generateBooleanSearch(term))
  }

  const handleUpgrade = () => {
    if (!enabled) return
    analytics.event({
      category: 'Search',
      action: 'Click Generate Boolean Upgrade',
      label: term
    })
    // Offers the free trial, so a logged-out click records the Basic trial intent
    // and resumes into checkout after signup (SCI-3378).
    loginActivateOrUpgrade(user, dispatch, undefined, { recordTrialIntent: true })
  }

  const onRowClick = isPremium ? handleGenerate : handleUpgrade

  return (
    <div
      data-showdropdown
      data-dropdown-item
      role='button'
      tabIndex={0}
      aria-disabled={!enabled}
      className={classNames(styles.generateRow, {
        [styles.generateRowDisabled]: !enabled
      })}
      onMouseDown={e => {
        // Prevent the search input from blurring so the dropdown stays open
        // through the loading/results states.
        e.preventDefault()
        onRowClick()
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onRowClick()
        }
      }}
    >
      <SparklesIcon className={styles.generateRowIcon} size={20} />
      <div className={styles.generateRowText}>
        <div className={styles.generateRowLabel}>
          {isReopenMode ? 'Choose a different AI-generated query' : 'Generate a boolean search'}
        </div>
        <div className={styles.generateRowSubtitle}>
          {isReopenMode
            ? 'Pick another option from the previous suggestions'
            : 'Choose an AI-built boolean query for your search'}
        </div>
      </div>
      {isPremium ? (
        <ChevronRightIcon className={styles.generateRowChevron} size='1em' />
      ) : (
        <Button
          color='primary'
          size='small'
          className={styles.generateRowUpgrade}
          onMouseDown={e => {
            // Prevent the row's onMouseDown from also firing, and prevent the
            // input from blurring so any follow-up modal can open cleanly.
            e.preventDefault()
            e.stopPropagation()
            handleUpgrade()
          }}
          disabled={!enabled}
        >
          {trialCtaLabel(user)}
        </Button>
      )}
    </div>
  )
}

const preventBlur = handler => e => {
  // Keep the search input focused so the dropdown's Fade stays expanded.
  e.preventDefault()
  handler(e)
}

const BooleanLoadingState = () => {
  const dispatch = useDispatch()
  const handleCancel = () => {
    analytics.event({ category: 'Search', action: 'Cancel Boolean Suggestions' })
    dispatch(clearBooleanSearchSuggestions())
  }
  return (
    <div className={styles.booleanPanel}>
      <div className={styles.booleanHeader}>
        <SparklesIcon className={styles.generateRowIcon} size={18} />
        <span>Generating boolean searches…</span>
      </div>
      <div className={styles.booleanSpinnerRow}>
        <CircleSpinner size={20} />
      </div>
      <div className={styles.booleanActions}>
        <button type='button' className={styles.booleanLinkBtn} onMouseDown={preventBlur(handleCancel)}>
          Cancel
        </button>
      </div>
    </div>
  )
}

const BooleanResultsState = ({ suggestions, onApply, onCancel }) => {
  const selectedQuery = suggestions.selectedVariant?.query || null
  return (
    <div className={styles.booleanPanel}>
      <div>
        <div className={styles.booleanHeader}>
          <SparklesIcon className={styles.generateRowIcon} size={18} />
          <span>Generated boolean searches</span>
        </div>
        {suggestions.originalTerm && (
          <div className={styles.booleanHeaderSubtitle}>
            Original query: <strong>{suggestions.originalTerm}</strong>
          </div>
        )}
      </div>
      {suggestions.variants.map((variant, idx) => {
        const isSelected = selectedQuery && variant.query === selectedQuery
        return (
          <div
            key={idx}
            data-showdropdown
            data-dropdown-item
            role={isSelected ? undefined : 'button'}
            tabIndex={isSelected ? -1 : 0}
            aria-disabled={isSelected || undefined}
            className={classNames(styles.booleanVariant, {
              [styles.booleanVariantDisabled]: isSelected
            })}
            onMouseDown={isSelected ? undefined : () => onApply(variant)}
            onKeyDown={
              isSelected
                ? undefined
                : e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onApply(variant)
                    }
                  }
            }
          >
            <div className={styles.booleanVariantQuery}>{variant.query}</div>
            <div className={styles.booleanVariantMeta}>
              {variant.label && <span className={styles.booleanVariantLabel}>{variant.label}</span>}
              {variant.label && variant.explanation && ' — '}
              {variant.explanation}
              {isSelected && <span className={styles.booleanVariantSelected}>Selected</span>}
            </div>
          </div>
        )
      })}
      <div className={styles.booleanActions}>
        <button type='button' className={styles.booleanLinkBtn} onMouseDown={preventBlur(onCancel)}>
          Use original query
        </button>
      </div>
    </div>
  )
}

const BooleanErrorState = ({ error, onRetry, onCancel }) => (
  <div className={styles.booleanPanel}>
    <div className={styles.booleanHeader}>
      <SparklesIcon className={styles.generateRowIcon} size={18} />
      <span>Boolean generation failed</span>
    </div>
    <div className={styles.booleanError}>{error}</div>
    <div className={styles.booleanActions}>
      <button type='button' className={styles.booleanLinkBtn} onMouseDown={preventBlur(onCancel)}>
        Use original query
      </button>
      <button type='button' className={styles.booleanLinkBtn} onMouseDown={preventBlur(onRetry)}>
        Try again
      </button>
    </div>
  </div>
)

export const Dropdown = ({ term, onClickResult, dropdownStyle, field, setDropdownOpen, showLinks = false, mode }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const autocompletes = useSelector(({ ui }) => ui.search.autocompletes)
  const maybeDoi = useSelector(({ ui }) => selectMaybeDoi(ui))
  const booleanSuggestions = useSelector(({ ui }) => ui.search.booleanSuggestions)
  const completions = autocompletes[field]
  const hasTerm = term && term.length >= 3

  // When the dropdown closes (Fade unmounts us), reset the active boolean
  // suggestions display so the next open starts in State A. We keep
  // selectedVariant so the "Choose a different…" CTA still appears on the
  // top row when the input still matches the previously-chosen query.
  useEffect(() => {
    return () => {
      dispatch(resetActiveBooleanSuggestions())
    }
  }, [dispatch])

  if (!hasTerm || maybeDoi) {
    return null
  }

  const goTo = route => history.push(route)

  const applyVariant = variant => {
    analytics.event({
      category: 'Search',
      action: 'Select Boolean Variant',
      label: variant.label || ''
    })
    // Snapshot the natural-language term + the full variant set BEFORE we
    // overwrite the input. The reducer stores this on `selectedVariant` so
    // the user can re-open the same suggestions later from the dropdown.
    dispatch(
      selectBooleanVariant({
        query: variant.query,
        originalTerm: booleanSuggestions?.originalTerm,
        variants: booleanSuggestions?.variants
      })
    )
    dispatch(setSearchTerm(variant.query))
    dispatch(setSearchFocused(false))
    dispatch(navSubmit())
  }

  const retryGenerate = () => {
    dispatch(generateBooleanSearch(term))
  }

  const cancelSuggestions = () => {
    analytics.event({ category: 'Search', action: 'Cancel Boolean Suggestions' })
    const original = booleanSuggestions?.originalTerm
    if (original && original !== (term || '').trim()) {
      // Re-opened state: the user is undoing their AI pick. Restore the
      // natural-language input and re-run the search with it.
      dispatch(setSearchTerm(original))
      dispatch(clearBooleanSearchSuggestions())
      dispatch(setSearchFocused(false))
      dispatch(navSubmit())
    } else {
      dispatch(clearBooleanSearchSuggestions())
    }
  }

  // While the boolean generation flow is active, the dropdown shows the
  // loading / results / error panel and hides the default rows below.
  if (booleanSuggestions?.loading) {
    return (
      <div className={classNames(styles.dropdown, dropdownStyle)}>
        <BooleanLoadingState />
      </div>
    )
  }

  if (booleanSuggestions?.error) {
    return (
      <div className={classNames(styles.dropdown, dropdownStyle)}>
        <BooleanErrorState error={booleanSuggestions.error} onRetry={retryGenerate} onCancel={cancelSuggestions} />
      </div>
    )
  }

  if (booleanSuggestions?.variants?.length) {
    return (
      <div className={classNames(styles.dropdown, dropdownStyle)}>
        <BooleanResultsState suggestions={booleanSuggestions} onApply={applyVariant} onCancel={cancelSuggestions} />
      </div>
    )
  }

  return (
    <div className={classNames(styles.dropdown, dropdownStyle)}>
      <BooleanSearchRow term={term} />
      {showLinks && (
        <>
          <SearchLink
            key='in-all'
            icon={<SearchIcon size={16} />}
            to={`/search?mode=all&q=${term}`}
            onMouseDown={() => goTo(`/search?mode=all&q=${term}`)}
          >
            Search <strong>{term}</strong>
          </SearchLink>
          {mode !== 'patents' && (
            <SearchLink
              key='in-authors'
              to={`/search?mode=all&author=${term}`}
              onMouseDown={() => goTo(`/search?mode=all&author=${term}`)}
            >
              Search <strong>{term}</strong> in authors
            </SearchLink>
          )}
          <SearchLink
            key='ask-question'
            icon={<HelpIcon size='1em' />}
            to={`/assistant?startTerm=${term}`}
            onMouseDown={() => {
              dispatch(setTab(TABS.assistant))
              goTo('/assistant')
            }}
          >
            Query <strong>{term}</strong> using assistant
          </SearchLink>
        </>
      )}
      {completions &&
        completions.length > 0 &&
        completions.map((autocomplete, index) => (
          <div
            key={index}
            data-showdropdown
            data-dropdown-item
            role='button'
            tabIndex='0'
            className={styles.dropdownItem}
            onMouseDown={() => {
              analytics.event({
                category: 'Search',
                action: 'Clicked autocomplete result',
                label: autocomplete.completion
              })
              onClickResult(`"${autocomplete.completion}"`)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                analytics.event({
                  category: 'Search',
                  action: 'Clicked autocomplete result',
                  label: autocomplete.completion
                })
                onClickResult(`"${autocomplete.completion}"`)
              }
            }}
          >
            <SearchIcon size={16} />
            <span
              className={styles.itemText}
              dangerouslySetInnerHTML={{
                __html: highlightText(autocomplete.completion, term, {
                  startTag: `<span class="${styles.match}">`,
                  endTag: '</span>'
                })
              }}
            />
          </div>
        ))}
    </div>
  )
}

export default Dropdown
